<template>
  <div class="checkout-page">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('orderPage.checkoutTitle')" :border="false" left-arrow @click-left="onClickLeft" />

    <div class="checkout-page__safe">
      <div class="safe-strip-icon" aria-hidden="true">
        <span class="safe-strip-icon__shield"></span>
      </div>
      <span>{{ $t('orderPage.transaksiAman') }}</span>
    </div>

    <div class="checkout-page__amount-card">
      <div>
        <div class="amount-card__label">{{ $t('orderPage.totalPembayaran') }}</div>
        <div class="amount-card__value">{{ priceUnit }} {{ totalPaymentAmount | numberFormat }}</div>
      </div>
      <div class="receipt-illustration">
        <div class="receipt-illustration__paper">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class="receipt-illustration__coin">Rp</div>
      </div>
      <div class="amount-card__divider"></div>
      <div
        class="section-title amount-card__detail-toggle"
        :class="{ 'amount-card__detail-toggle--expanded': detailExpanded }"
        @click="detailExpanded = !detailExpanded"
      >
        <span>{{ $t('orderPage.rincianPesanan') }}</span>
        <van-icon name="arrow-down" />
      </div>
      <transition name="details-expand">
        <div v-if="detailExpanded" class="details-body amount-card__details-body">
          <div class="checkout-product">
            <div class="checkout-product__image" :class="{ 'checkout-product__image--empty': !checkoutProductImage }">
              <img v-if="checkoutProductImage" :src="checkoutProductImage" :alt="checkoutProductName" />
            </div>
            <div class="checkout-product__info">
              <div class="checkout-product__name">{{ checkoutProductName }}</div>
              <div class="checkout-product__sku">{{ checkoutSkuText }}</div>
            </div>
          </div>
          <div class="detail-row">
            <span>{{ $t('orderPage.securityDeposit') }}</span>
            <span>{{ priceUnit }}{{ tempOrderData.depositAmount | numberFormat }}</span>
          </div>
          <div class="detail-row">
            <span>{{ $t('orderPage.firstRentalAmount') }}</span>
            <span>{{ priceUnit }}{{ firstRentalAmount | numberFormat }}</span>
          </div>
          <div v-if="isCodSelected" class="detail-row">
            <span>{{ $t('orderPage.codShippingFee') }}</span>
            <span>{{ priceUnit }}{{ codShippingFeeAmount | numberFormat }}</span>
          </div>
          <div v-if="isCodSelected" class="detail-divider"></div>
          <div v-if="isCodSelected" class="detail-row detail-row--strong">
            <span>{{ $t('orderPage.dueAfterApproval') }}</span>
            <span>{{ priceUnit }}{{ dueAfterApprovalAmount | numberFormat }}</span>
          </div>
          <div v-if="isCodSelected" class="detail-row detail-row--strong">
            <span>{{ $t('orderPage.dueOnDelivery') }}</span>
            <span>{{ priceUnit }}{{ dueOnDeliveryAmount | numberFormat }}</span>
          </div>
          <div v-if="!isCodSelected" class="detail-row detail-row--strong">
            <span>{{ $t('orderPage.amountDueToday') }}</span>
            <span>{{ priceUnit }}{{ totalAmount | numberFormat }}</span>
          </div>
          <div v-if="showExtendedOrderDetails" class="detail-row">
            <span>{{ $t('orderPage.remainingRent') }}</span>
            <span>{{ tempOrderData.planIndex - 1 }} {{ $t('orderPage.payments') }} * {{ priceUnit }}{{ tempOrderData.planPrice | numberFormat }}</span>
          </div>
          <div v-if="showExtendedOrderDetails" class="detail-row">
            <span>{{ $t('orderPage.rentalFrequency') }}</span>
            <span>{{ rentalCycleText }}</span>
          </div>
        </div>
      </transition>
    </div>

    <div class="checkout-page__payment-section">
      <div class="section-heading">{{ $t('orderPage.payMethodTitle') }}</div>
      <PayMethodSelector :value.sync="selectedPayMethod" :cod-enabled="codEnabled" :store-enabled="storeEnabled" :online-methods="onlinePayMethods" />
    </div>

    <transition name="store-expand" @after-enter="scrollToStoreSection">
    <section v-if="isStoreSelected" ref="storeSection" class="checkout-page__store-section">
      <div class="store-section__heading">{{ $t('orderPage.pickupStoreTitle') }}</div>
      <div v-if="storesLoading" class="store-section__state">{{ $t('loading') }}</div>
      <div v-else-if="storesError" class="store-section__state store-section__state--error"><span>{{ storesError }}</span><button type="button" @click="loadStores">{{ $t('retry') }}</button></div>
      <div v-else-if="!stores.length" class="store-section__state">{{ $t('orderPage.storeEmpty') }}</div>
      <div v-else class="store-list">
        <button v-for="store in stores" :key="store.id" type="button" class="store-option" :class="{ 'store-option--selected': selectedStoreId === String(store.id) }" @click="selectStore(store)">
          <van-icon name="shop-o" />
          <span class="store-option__content"><strong>{{ store.name }}</strong><small>{{ store.address }}</small><small v-if="store.businessHour">{{ store.businessHour }}</small></span>
          <van-icon :name="selectedStoreId === String(store.id) ? 'checked' : 'circle'" />
        </button>
      </div>
    </section>
    </transition>

    <div v-if="isCodSelected" class="checkout-page__cod-notice">
      <div class="cod-notice__icon">
        <span></span>
      </div>
      <span>{{ $t('orderPage.codNotice') }}</span>
    </div>

    <div v-if="isCodSelected" class="checkout-page__cod-fee-notice">
      <van-icon name="info-o" />
      <div class="cod-fee-notice__content">
        <div class="cod-fee-notice__title">{{ $t('orderPage.codShippingFeeNoticeTitle') }}</div>
        <div class="cod-fee-notice__desc">{{ $t('orderPage.codShippingFeeNoticeDesc') }}</div>
        <div class="cod-fee-notice__desc">{{ $t('orderPage.codShippingFeeNoticeRefund') }}</div>
      </div>
    </div>

    <div class="checkout-page__safe-card">
      <div class="safe-card__icon"><van-icon name="shield-o" /></div>
      <div>
        <div class="safe-card__title">{{ $t('orderPage.belanjaAmanTitle') }}</div>
        <div class="safe-card__desc">{{ safeCardDesc }}</div>
      </div>
    </div>

    <div class="checkout-page__footer">
      <div class="footer-total">
        <div class="footer-total__label">{{ $t('orderPage.totalPembayaran') }}</div>
        <div class="footer-total__value">{{ priceUnit }} {{ totalPaymentAmount | numberFormat }}</div>
      </div>
      <div class="checkout-btn" :class="{ 'checkout-btn--cod': isCodSelected, 'checkout-btn--store': isStoreSelected, 'btn-disabled': submitting }" @click="onSubmit">
        <van-icon name="lock" />
        <span>{{ submitting ? $t('loading') : submitText }}</span>
      </div>
    </div>

    <KycPopup ref="kycPopup" />
    <AuthResultPopup
      ref="authResultPopup"
      @success="handleAuthResultSuccess"
      @retry="handleAuthResultRetry"
      @back="handleAuthResultBack"
      @close="clearAuthResultQuery"
    />

    <van-popup
      v-model="codVerificationVisible"
      position="bottom"
      round
      :close-on-click-overlay="!submitting"
      :safe-area-inset-bottom="true"
      @click-overlay="closeCodVerification"
    >
      <div class="cod-verification" :class="{ 'cod-verification--store': isStoreSelected }">
        <div v-if="isStoreSelected" class="cod-verification__handle"></div>
        <div class="cod-verification__header" :class="{ 'cod-verification__header--store': isStoreSelected }">
          <div class="cod-verification__header-copy">
            <div v-if="isStoreSelected" class="cod-verification__store-icon"><van-icon name="shop-o" /></div>
            <div>
            <div class="cod-verification__title">{{ verificationTitle }}</div>
            <div class="cod-verification__desc">{{ verificationDesc }}</div>
            </div>
          </div>
          <button type="button" class="cod-verification__close" :class="{ 'cod-verification__close--store': isStoreSelected }" @click="closeCodVerification"><van-icon name="cross" /></button>
        </div>
        <div class="cod-verification__phone">
          <span>{{ $t('orderPage.codPhoneLabel') }}</span>
          <strong>{{ maskedCodPhone }}</strong>
        </div>
        <div class="cod-verification__label">{{ $t('orderPage.codVerificationCode') }}</div>
        <div class="cod-verification__code-row">
          <input
            :value="codVerificationCode"
            type="tel"
            inputmode="numeric"
            maxlength="6"
            :placeholder="$t('orderPage.codVerificationCodePlaceholder')"
            @input="onCodCodeInput"
          />
          <button
            type="button"
            :disabled="sendingCodCode || codCodeCountdown > 0"
            @click="sendCodVerificationCode"
          >
            {{ codSendCodeText }}
          </button>
        </div>
        <div class="cod-verification__tip">{{ $t('orderPage.codCodeTip') }}</div>
        <button
          type="button"
          class="cod-verification__confirm"
          :disabled="!canConfirmCod || submitting"
          @click="confirmCodOrder"
        >
          {{ submitting ? $t('loading') : verificationConfirmText }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Icon, Popup } from 'vant';
import PayMethodSelector from '@/components/PayMethodSelector.vue';
import KycPopup from '@/components/KycPopup/index.vue';
import AuthResultPopup from '@/components/AuthResultPopup/index.vue';
import { createOrder } from '@/api/order';
import { getCode, getAppSystemConfig, getStores } from '@/api/common';
import { getAuthResult } from '@/api/kyc';
import {
  clearFinishedKycV2,
  completeKycV2Flow,
  openKycV2ResultPopup,
  retryKycV2Flow,
  startKycV2Flow
} from '@/utils/kycV2Flow';
import { hasKycV2Context, isKycV2Return } from '@/utils/kycV2';
import { PAY_METHOD } from '@/common/constants';
import { resolveAssetUrl } from '@/common/product';
import { DEFAULT_REG_CHANNEL } from '@/config';

const KYC_AUTH_TOKEN_KEY = 'checkoutKycAuthToken';
const TEMP_ORDER_KEY = 'checkoutTempOrderData';
const SAVED_PAY_METHOD_KEY = 'checkoutSavedPayMethod';
const SAVED_STORE_ID_KEY = 'checkoutSelectedStoreId';
const KYC_VERIFIED_KEY = 'checkoutKycVerified';
const LATEST_ORDER_ID_KEY = 'checkoutLatestOrderId';
const THIRD_PARTY_RETURN_QUERY = 'fromThirdParty';
const COD_ORDER_CODE_SCENE = 'order';
const COD_CODE_COUNTDOWN_SECONDS = 60;

const ONLINE_PAY_CHANNELS = {
  ayolinx: { titleKey: 'orderPage.onlinePayQrisTitle', descKey: 'orderPage.onlinePayQrisDesc', iconType: 'qris', tags: [] },
  ayolinx_paylink: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] },
  paycools: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] }
};

export default {
  name: 'Checkout',
  components: {
    PayMethodSelector,
    KycPopup,
    AuthResultPopup,
    'van-icon': Icon,
    [Popup.name]: Popup
  },
  data() {
    return {
      PAY_METHOD,
      selectedPayMethod: PAY_METHOD.ONLINE,
      codEnabled: false,
      storeEnabled: false,
      onlinePayMethods: [],
      stores: [],
      storesLoading: false,
      storesError: '',
      selectedStoreId: '',
      systemConfigLoaded: false,
      submitting: false,
      detailExpanded: true,
      showExtendedOrderDetails: false,
      codVerificationVisible: false,
      codVerificationCode: '',
      sendingCodCode: false,
      codCodeCountdown: 0,
      codCountdownTimer: null
    }
  },
  computed: {
    ...mapState({
      tempOrderData: state => state.orders.tempOrderData || {},
      checkoutPayMethod: state => state.orders.checkoutPayMethod || '',
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL,
      userInfo: state => state.user.userInfo || {}
    }),
    priceUnit() {
      return this.$t('priceUnit');
    },
    totalAmount() {
      return this.tempOrderData.firstPayAmount || 0;
    },
    firstRentalAmount() {
      return this.tempOrderData.firstRentalAmount || this.addMoney(this.tempOrderData.planPrice, this.tempOrderData.riskAmount);
    },
    codShippingFeeAmount() {
      if (!this.isCodSelected) return 0;
      return Number(this.tempOrderData?.goodsInfo?.codShippingFee || this.tempOrderData?.codShippingFee || 0);
    },
    dueAfterApprovalAmount() {
      return this.codShippingFeeAmount;
    },
    dueOnDeliveryAmount() {
      return this.addMoney(this.tempOrderData.depositAmount, this.firstRentalAmount);
    },
    totalPaymentAmount() {
      return this.isCodSelected ? this.addMoney(this.dueAfterApprovalAmount, this.dueOnDeliveryAmount) : this.totalAmount;
    },
    checkoutProductName() {
      return this.tempOrderData?.goodsInfo?.goodName || this.tempOrderData?.goodsInfo?.name || this.$t('product');
    },
    checkoutProductImage() {
      const skuImage = this.tempOrderData?.sku?.imageUrl;
      const goodsInfo = this.tempOrderData?.goodsInfo || {};
      return resolveAssetUrl(skuImage || goodsInfo.imageUrl || goodsInfo.goodsPic || goodsInfo.imgUrl || '');
    },
    checkoutSkuText() {
      return this.tempOrderData.skuStr || '';
    },
    rentalCycleText() {
      const cycle = this.tempOrderData?.sku?.rentalCycle || this.tempOrderData.planUnit;
      return cycle ? this.$t(`cycle.${cycle}`) : '';
    },
    isCodSelected() {
      return this.codEnabled && this.selectedPayMethod === PAY_METHOD.COD;
    },
    isStoreSelected() {
      return this.storeEnabled && this.selectedPayMethod === PAY_METHOD.STORE;
    },
    submitText() {
      if (this.isCodSelected) return this.$t('orderPage.checkoutCod');
      if (this.isStoreSelected) return this.$t('orderPage.checkoutStore');
      return this.$t('orderPage.checkoutOnline');
    },
    safeCardDesc() {
      return this.$t('orderPage.belanjaAmanDesc');
    },
    userPhoneNumber() {
      return (this.userInfo?.phoneNumber || '').trim();
    },
    maskedCodPhone() {
      const phone = this.userPhoneNumber;
      if (!phone || phone.length <= 7) return phone || '--';
      return `${phone.slice(0, 3)}****${phone.slice(-4)}`;
    },
    verificationTitle() {
      return this.isStoreSelected ? this.$t('orderPage.storeVerifyPhoneTitle') : this.$t('orderPage.codVerifyPhoneTitle');
    },
    verificationDesc() {
      return this.isStoreSelected ? this.$t('orderPage.storeVerifyPhoneDesc') : this.$t('orderPage.codVerifyPhoneDesc');
    },
    verificationConfirmText() {
      return this.isStoreSelected ? this.$t('orderPage.storeConfirmOrder') : this.$t('orderPage.codConfirmOrder');
    },
    canConfirmCod() {
      return this.codVerificationCode.length === 6;
    },
    codSendCodeText() {
      if (this.sendingCodCode) return this.$t('loading');
      if (this.codCodeCountdown > 0) {
        return this.$t('orderPage.codCodeCountdown', { seconds: this.codCodeCountdown });
      }
      return this.$t('orderPage.codGetCode');
    }
  },
  watch: {
    selectedPayMethod(val) {
      if (val === PAY_METHOD.COD || val === PAY_METHOD.STORE || this.isValidOnlinePayMethod(val)) {
        this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', val);
      }
      if (val === PAY_METHOD.STORE) {
        this.loadStores();
      }
    }
  },
  mounted() {
    if (!this.tempOrderData?.goodsInfo) {
      this.restoreTempOrderData();
    }
    if (!this.tempOrderData?.goodsInfo) {
      this.$router.replace('/index');
      return;
    }
    this.scrollToTop();
    this.selectedStoreId = sessionStorage.getItem(SAVED_STORE_ID_KEY) || '';
    this.loadGoodsCodConfig();
    this.restoreCheckoutPayMethod();
    this.loadSystemFeatures();
    this.handleKycRedirect();
  },
  beforeRouteUpdate(to, from, next) {
    this.scrollToTop();
    next();
  },
  beforeDestroy() {
    this.clearCodCountdownTimer();
  },
  methods: {
    ...mapActions('orders', ['clearTempOrder']),
    onClickLeft() {
      sessionStorage.removeItem(TEMP_ORDER_KEY);
      sessionStorage.removeItem(SAVED_PAY_METHOD_KEY);
      sessionStorage.removeItem(SAVED_STORE_ID_KEY);
      sessionStorage.removeItem(KYC_VERIFIED_KEY);
      this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', '');
      this.$router.replace('/order/confirm');
    },
    buildContactIds() {
      return (this.tempOrderData.emergencyContactList || [])
        .map(item => item.id)
        .filter(Boolean);
    },
    buildOrderParams(verificationCode = '') {
      const {
        addressId,
        depositAmount,
        firstPayAmount,
        goodsSkuId,
        planIndex,
        planPrice,
        planUnit,
        backDate,
        rentDate
      } = this.tempOrderData;
      return {
        channel: this.channel || DEFAULT_REG_CHANNEL,
        addressId,
        depositAmount,
        firstPayAmount,
        goodsSkuId,
        planIndex,
        planPrice,
        planUnit,
        backDate,
        rentDate,
        returnUrl: this.buildOrderDetailReturnUrl(),
        payMethod: this.isCodSelected ? PAY_METHOD.COD : (this.isStoreSelected ? PAY_METHOD.STORE : PAY_METHOD.ONLINE),
        ...(this.isValidOnlinePayMethod(this.selectedPayMethod) ? { payChannel: this.selectedPayMethod } : {}),
        ...(this.isStoreSelected ? { storeId: this.selectedStoreId } : {}),
        contactIds: this.buildContactIds(),
        ...(verificationCode ? { code: verificationCode } : {})
      };
    },
    buildOrderDetailReturnUrl() {
      const hasWindowLocation = typeof window !== 'undefined' && window.location;
      const url = new URL('/rental-h5/order/detail', hasWindowLocation ? window.location.origin : '/');
      url.searchParams.set(THIRD_PARTY_RETURN_QUERY, '1');
      return hasWindowLocation ? url.toString() : `${url.pathname}${url.search}`;
    },
    saveLatestOrderId(orderId) {
      const value = String(orderId || '').trim();
      if (!value) {
        return;
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(LATEST_ORDER_ID_KEY, value);
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LATEST_ORDER_ID_KEY, value);
      }
    },
    addMoney(...amounts) {
      return amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    disableCodSelection() {
      this.codEnabled = false;
      if (this.selectedPayMethod === PAY_METHOD.COD) {
        const fallback = this.getDefaultOnlinePayMethod();
        this.selectedPayMethod = fallback;
        this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', fallback);
      }
    },
    loadGoodsCodConfig() {
      const value = this.tempOrderData?.goodsInfo?.codEnabled;
      this.codEnabled = value === true || value === 1 || value === '1' || value === 'true';
      if (!this.codEnabled) this.disableCodSelection();
    },
    getDefaultOnlinePayMethod() {
      return this.onlinePayMethods[0]?.key || PAY_METHOD.ONLINE;
    },
    getDefaultPayMethod() {
      if (this.onlinePayMethods.length) return this.onlinePayMethods[0].key;
      if (this.codEnabled) return PAY_METHOD.COD;
      return this.storeEnabled ? PAY_METHOD.STORE : PAY_METHOD.ONLINE;
    },
    isValidOnlinePayMethod(value) {
      return !!value && this.onlinePayMethods.some(item => item.key === value);
    },
    async loadSystemFeatures() {
      if (this.systemConfigLoaded) return;
      try {
        const res = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL });
        const payments = res?.data?.features?.payments || [];
        this.storeEnabled = Array.isArray(payments) && payments.includes(PAY_METHOD.STORE);
        const methods = (Array.isArray(payments) ? payments : [])
          .map(key => {
            const config = ONLINE_PAY_CHANNELS[key];
            if (!config) return null;
            return {
              key,
              title: this.$t(config.titleKey),
              desc: this.$t(config.descKey),
              iconType: config.iconType,
              tags: config.tags
            };
          })
          .filter(Boolean);
        this.onlinePayMethods = methods;
        this.$nextTick(() => this.restoreCheckoutPayMethod());
      } catch (e) {
        console.warn('Failed to load system features:', e);
      } finally {
        this.systemConfigLoaded = true;
      }
    },
    restoreTempOrderData() {
      try {
        const savedData = sessionStorage.getItem(TEMP_ORDER_KEY);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (parsed?.goodsInfo) {
            this.$store.commit('orders/SET_TEMP_ORDER_DATA', parsed);
          }
        }
      } catch (e) {
        console.warn('Failed to restore temp order data:', e);
      }
    },
    restoreCheckoutPayMethod() {
      const candidate = this.checkoutPayMethod || this.$route.query.payMethod || '';
      const payMethod = candidate.includes('?') ? candidate.split('?')[0] : candidate;
      if (this.isValidOnlinePayMethod(payMethod)) {
        this.selectedPayMethod = payMethod;
        return;
      }
      if (payMethod === PAY_METHOD.STORE && this.storeEnabled) {
        this.selectedPayMethod = PAY_METHOD.STORE;
        return;
      }
      this.selectedPayMethod = this.getDefaultPayMethod();
    },
    clearCodCountdownTimer() {
      if (this.codCountdownTimer) {
        clearInterval(this.codCountdownTimer);
        this.codCountdownTimer = null;
      }
    },
    startCodCountdown() {
      this.clearCodCountdownTimer();
      this.codCodeCountdown = COD_CODE_COUNTDOWN_SECONDS;
      this.codCountdownTimer = setInterval(() => {
        if (this.codCodeCountdown <= 1) {
          this.codCodeCountdown = 0;
          this.clearCodCountdownTimer();
          return;
        }
        this.codCodeCountdown -= 1;
      }, 1000);
    },
    openCodVerification() {
      this.codVerificationCode = '';
      this.codVerificationVisible = true;
    },
    closeCodVerification() {
      if (this.submitting) return;
      this.codVerificationVisible = false;
    },
    onCodCodeInput(event) {
      this.codVerificationCode = (event.target.value || '').replace(/\D/g, '').slice(0, 6);
      event.target.value = this.codVerificationCode;
    },
    async sendCodVerificationCode() {
      const mobile = this.userPhoneNumber;
      if (!mobile) {
        this.$toast(this.$t('orderPage.codPhoneMissing'));
        return;
      }
      if (this.sendingCodCode || this.codCodeCountdown > 0) return;
      this.sendingCodCode = true;
      try {
        const res = await getCode({
          mobile,
          scene: COD_ORDER_CODE_SCENE
        });
        if (res?.code === 'Success') {
          this.startCodCountdown();
          this.$toast(this.$t('orderPage.codCodeSent'));
        } else {
          this.$toast(res?.message || this.$t('orderPage.codCodeSendFailed'));
        }
      } catch (e) {
        this.$toast(this.$t('orderPage.codCodeSendFailed'));
      } finally {
        this.sendingCodCode = false;
      }
    },
    scrollToStoreSection() {
      const section = this.$refs.storeSection;
      if (section?.scrollIntoView) {
        section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async loadStores() {
      if (this.storesLoading) return;
      this.storesLoading = true;
      this.storesError = '';
      try {
        const res = await getStores();
        if (res?.code !== 'Success' || !Array.isArray(res.data)) {
          throw new Error(res?.message || this.$t('orderPage.storeLoadFailed'));
        }
        this.stores = res.data.filter(Boolean);
        if (!this.stores.some(store => String(store.id) === this.selectedStoreId)) {
          this.selectedStoreId = '';
        }
      } catch (e) {
        this.storesError = e?.message || this.$t('orderPage.storeLoadFailed');
      } finally {
        this.storesLoading = false;
      }
    },
    selectStore(store) {
      if (!store?.id) return;
      this.selectedStoreId = String(store.id);
      sessionStorage.setItem(SAVED_STORE_ID_KEY, this.selectedStoreId);
    },
    getKycRedirectToken() {
      const fromQuery = this.$route.query.token || this.$route.query.bizToken || this.$route.query.biz_token;
      if (fromQuery) return fromQuery;
      const payMethod = this.$route.query.payMethod;
      if (payMethod && payMethod.includes('?token=')) {
        const extracted = payMethod.split('?token=')[1];
        if (extracted) return extracted;
      }
      return sessionStorage.getItem(KYC_AUTH_TOKEN_KEY);
    },
    clearAuthResultQuery() {
      const { authType, token, bizToken, biz_token, ...query } = this.$route.query;
      if (authType || token || bizToken || biz_token) {
        this.$router.replace({ path: this.$route.path, query });
      }
    },
    async handleKycRedirect() {
      if (isKycV2Return(this.$route)) {
        try {
          const view = await completeKycV2Flow(this);
          if (view && !openKycV2ResultPopup(this, view)) {
            clearFinishedKycV2(this);
          }
        } catch (e) {
          console.error('Complete KYC v2 failed:', e);
          this.$toast(this.$t('systemError'));
          clearFinishedKycV2(this);
        }
        return;
      }
      const authType = this.$route.query.authType;
      if (!authType) {
        return;
      }
      const token = this.getKycRedirectToken();
      if (!token) {
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
        this.clearAuthResultQuery();
        return;
      }
      try {
        const res = await getAuthResult({ token, type: authType });
        if (res.code !== 'Success') {
          this.$toast({ message: res.message || this.$t('systemError'), duration: 3000 });
          this.clearAuthResultQuery();
          return;
        }
        const verifyResult = res.data?.verifyResult;
        if (verifyResult) {
          this.$refs.authResultPopup.open({
            authType: res.data?.type || authType,
            verifyResult
          });
        } else {
          this.clearAuthResultQuery();
        }
      } catch (e) {
        console.error('Get auth result failed:', e);
        this.$toast(this.$t('systemError'));
        this.clearAuthResultQuery();
      }
    },
    async handleAuthResultSuccess() {
      sessionStorage.removeItem(KYC_AUTH_TOKEN_KEY);
      sessionStorage.setItem(KYC_VERIFIED_KEY, '1');
      clearFinishedKycV2(this);
      this.clearAuthResultQuery();
      if (this.isCodSelected || this.isStoreSelected) {
        this.openCodVerification();
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.submitOrder();
      } catch (e) {
        console.error('Online checkout after KYC failed:', e);
        this.$toast(this.$t('systemError'));
      } finally {
        this.submitting = false;
      }
    },
    async handleAuthResultRetry() {
      this.clearAuthResultQuery();
      if (hasKycV2Context()) {
        await retryKycV2Flow(this);
        return;
      }
      await this.ensureKycReady();
    },
    handleAuthResultBack() {
      sessionStorage.removeItem(KYC_AUTH_TOKEN_KEY);
      sessionStorage.removeItem(SAVED_STORE_ID_KEY);
      this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', '');
      clearFinishedKycV2(this);
      this.clearAuthResultQuery();
      this.$router.replace('/order/confirm');
    },
    async ensureKycReady() {
      if (sessionStorage.getItem(KYC_VERIFIED_KEY) === '1') {
        return true;
      }
      this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', this.selectedPayMethod);
      sessionStorage.setItem(TEMP_ORDER_KEY, JSON.stringify(this.tempOrderData));
      sessionStorage.setItem(SAVED_PAY_METHOD_KEY, this.selectedPayMethod);
      if (this.isStoreSelected && this.selectedStoreId) {
        sessionStorage.setItem(SAVED_STORE_ID_KEY, this.selectedStoreId);
      }
      return startKycV2Flow(this, {
        businessScene: 'checkout',
        returnPath: '/payment/checkout',
        returnQuery: { payMethod: this.selectedPayMethod }
      });
    },
    getOrderIdFromUrl(url = '') {
      const rawUrl = String(url || '').trim();
      if (!rawUrl) return '';
      try {
        return new URL(rawUrl, window.location.origin).searchParams.get('orderId') || '';
      } catch (e) {
        const match = rawUrl.match(/[?&]orderId=([^&#]+)/i);
        return match ? decodeURIComponent(match[1]) : '';
      }
    },
    async submitOrder(verificationCode = '') {
      const res = await createOrder(this.buildOrderParams(verificationCode)).catch(err => err);
      if (res?.code === 'Success') {
        const orderId = res.data?.orderId || this.getOrderIdFromUrl(res.data?.url);
        if (!orderId) {
          this.$toast(this.$t('orderPage.orderFailed'));
          return;
        }
        this.saveLatestOrderId(orderId);
        this.$toast(this.$t('orderPage.orderSuccess'));
        if (this.isCodSelected) {
          this.clearTempOrder();
          sessionStorage.removeItem(TEMP_ORDER_KEY);
          sessionStorage.removeItem(SAVED_PAY_METHOD_KEY);
          sessionStorage.removeItem(SAVED_STORE_ID_KEY);
          sessionStorage.removeItem(KYC_VERIFIED_KEY);
          this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', '');
          this.codVerificationVisible = false;
          this.$router.replace(`/payment/cod-success?orderId=${orderId}`);
          return;
        }
        if (this.isStoreSelected) {
          const storeId = this.selectedStoreId;
          this.clearTempOrder();
          sessionStorage.removeItem(TEMP_ORDER_KEY);
          sessionStorage.removeItem(SAVED_PAY_METHOD_KEY);
          sessionStorage.removeItem(SAVED_STORE_ID_KEY);
          sessionStorage.removeItem(KYC_VERIFIED_KEY);
          this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', '');
          this.codVerificationVisible = false;
          this.$router.replace(`/payment/offline-pickup-success?orderId=${orderId}&storeId=${encodeURIComponent(storeId)}`);
          return;
        }
        this.clearTempOrder();
        sessionStorage.removeItem(TEMP_ORDER_KEY);
        sessionStorage.removeItem(SAVED_PAY_METHOD_KEY);
        sessionStorage.removeItem(SAVED_STORE_ID_KEY);
        sessionStorage.removeItem(KYC_VERIFIED_KEY);
        this.$store.commit('orders/SET_CHECKOUT_PAY_METHOD', '');
        this.$router.replace(`/order/detail?orderId=${orderId}`);
      } else {
        this.$toast(res?.message || this.$t('orderPage.orderFailed'));
      }
    },
    async onSubmit() {
      if (this.submitting) return;
      if (this.selectedPayMethod === PAY_METHOD.COD && !this.codEnabled) {
        this.disableCodSelection();
        return;
      }
      if (this.selectedPayMethod === PAY_METHOD.STORE && (!this.storeEnabled || !this.selectedStoreId)) {
        this.$toast(this.$t('orderPage.storeRequired'));
        return;
      }
      this.submitting = true;
      try {
        const kycReady = await this.ensureKycReady();
        if (!kycReady) {
          this.submitting = false;
          return;
        }
        if (this.isCodSelected || this.isStoreSelected) {
          this.openCodVerification();
          this.submitting = false;
          return;
        }
        await this.submitOrder();
      } catch (e) {
        console.error('Checkout submit failed:', e);
        this.$toast(this.$t('systemError'));
      } finally {
        this.submitting = false;
      }
    },
    async confirmCodOrder() {
      if ((this.isCodSelected && !this.codEnabled) || (this.isStoreSelected && !this.storeEnabled)) {
        this.disableCodSelection();
        this.codVerificationVisible = false;
        return;
      }
      if (!this.canConfirmCod) {
        this.$toast(this.$t('orderPage.codCodeRequired'));
        return;
      }
      if (this.isStoreSelected && !this.selectedStoreId) {
        this.$toast(this.$t('orderPage.storeRequired'));
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.submitOrder(this.codVerificationCode);
      } catch (e) {
        console.error('COD submit failed:', e);
        this.$toast(this.$t('systemError'));
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style lang="less" scoped>
.checkout-page {
  min-height: 100vh;
  padding: 118px 30px 270px;
  box-sizing: border-box;
  background: #FFFFFF;

  ::v-deep .van-nav-bar {
    height: 108px;
    line-height: 108px;
    background: #FFFFFF;
  }

  ::v-deep .van-nav-bar__title {
    font-size: 36px;
    font-weight: 700;
    color: #0B0B12;
  }

  ::v-deep .van-nav-bar .van-icon {
    font-size: 42px;
    color: #0B0B12;
  }

  &__safe {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 64px;
    padding: 0 32px;
    border-radius: 22px;
    background: linear-gradient(100deg, #F4FCF8 0%, #EFF8F4 100%);
    color: #2FA76F;
    font-size: 28px;
    line-height: 36px;
    box-sizing: border-box;

  }

  &__amount-card,
  &__safe-card {
    margin-top: 24px;
    background: #FFFFFF;
    border: 1px solid #EEF0F5;
    border-radius: 24px;
    box-shadow: 0 12px 30px rgba(24, 37, 72, 0.10);
  }

  &__amount-card {
    position: relative;
    min-height: 238px;
    padding: 30px 38px 0;
    box-sizing: border-box;
    overflow: hidden;
  }

  &__payment-section {
    margin-top: 28px;
  }

  &__store-section {
    margin-top: 20px;
    padding: 26px;
    border: 1px solid #E6EAF2;
    border-radius: 22px;
    background: #FFFFFF;
  }

  &__cod-notice {
    display: flex;
    align-items: center;
    gap: 22px;
    margin-top: 24px;
    min-height: 96px;
    padding: 20px 30px;
    border-radius: 16px;
    background: linear-gradient(100deg, #FFF7E9 0%, #FFFDF8 100%);
    color: #6B3B00;
    font-size: 26px;
    line-height: 42px;
    box-sizing: border-box;
  }

  &__cod-fee-notice {
    display: flex;
    align-items: flex-start;
    gap: 22px;
    margin-top: 18px;
    padding: 20px 30px;
    border: 0;
    border-radius: 16px;
    background: linear-gradient(100deg, #FFF7E9 0%, #FFFDF8 100%);
    color: #6B3B00;
    box-sizing: border-box;

    .van-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 54px;
      height: 54px;
      margin-top: 1px;
      border-radius: 14px;
      background: linear-gradient(135deg, #FFB45F, #FF6A00);
      color: #FFFFFF;
      font-size: 30px;
    }
  }

  &__safe-card {
    display: flex;
    align-items: center;
    gap: 22px;
    min-height: 100px;
    padding: 20px 30px;
    border: 0;
    border-radius: 18px;
    background: linear-gradient(100deg, #F2F7FF 0%, #FAFCFF 100%);
    box-shadow: none;
    box-sizing: border-box;

    > .van-icon:last-child {
      margin-left: auto;
      color: #0B0B12;
      font-size: 30px;
      font-weight: 700;
    }
  }

  &__footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 22px 34px 36px;
    background: #FFFFFF;
    box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
  }
}

.safe-strip-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  flex-shrink: 0;

  &__shield {
    position: relative;
    width: 25px;
    height: 29px;
    border-radius: 13px 13px 10px 10px;
    background: #28A96B;
    clip-path: polygon(50% 0, 92% 14%, 84% 72%, 50% 100%, 16% 72%, 8% 14%);

    &::after {
      position: absolute;
      content: '';
      left: 7px;
      top: 10px;
      width: 10px;
      height: 6px;
      border-left: 3px solid #FFFFFF;
      border-bottom: 3px solid #FFFFFF;
      transform: rotate(-45deg);
    }
  }
}

.amount-card {
  &__label {
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: #6B7285;
  }

  &__value {
    margin-top: 16px;
    font-size: 54px;
    line-height: 62px;
    font-weight: 400;
    color: #0B45FF;
  }

  &__divider {
    margin-top: 28px;
    border-top: 1px solid #E3E6ED;
  }

  &__detail-toggle {
    height: 78px;

    .van-icon {
      transition: transform 0.26s ease;
    }

    &--expanded .van-icon {
      transform: rotate(180deg);
    }
  }

  &__details-body {
    margin: 0 0 30px;
    padding-top: 4px;
    transform-origin: top center;
  }
}

.receipt-illustration {
  position: absolute;
  right: 42px;
  top: 30px;
  width: 170px;
  height: 150px;

  &__paper {
    position: absolute;
    right: 36px;
    top: 0;
    width: 104px;
    height: 126px;
    border: 8px solid rgba(37, 101, 242, 0.60);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(243, 248, 255, 0.90), rgba(255, 255, 255, 0.85));

    &::before,
    &::after {
      position: absolute;
      content: '';
      top: -8px;
      width: 18px;
      height: 18px;
      border-radius: 0 0 10px 10px;
      background: #FFFFFF;
      border-left: 6px solid rgba(37, 101, 242, 0.60);
      border-right: 6px solid rgba(37, 101, 242, 0.60);
      border-bottom: 6px solid rgba(37, 101, 242, 0.60);
    }

    &::before { left: 28px; }
    &::after { right: 20px; }

    span {
      display: block;
      width: 46px;
      height: 8px;
      margin: 28px auto 0;
      border-radius: 8px;
      background: rgba(37, 101, 242, 0.48);

      & + span {
        margin-top: 14px;
      }
    }
  }

  &__coin {
    position: absolute;
    right: 0;
    top: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: linear-gradient(135deg, #5AA2FF, #1259F3);
    color: #FFFFFF;
    font-size: 30px;
    font-weight: 700;
    box-shadow: 0 10px 20px rgba(18, 89, 243, 0.28);
  }
}

.section-title,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  color: #1D2437;

  .van-icon {
    font-size: 34px;
    color: #0B1635;
  }
}

.section-heading {
  margin-bottom: 20px;
  color: #70758C;
  text-transform: none;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 26px;
  line-height: 36px;
  color: #6B7285;

  & + & {
    margin-top: 16px;
  }

  span:last-child {
    color: #020B3A;
    text-align: right;
    font-weight: 400;
  }

  &--strong {
    span:last-child {
      color: #0B45FF;
      font-weight: 400;
    }
  }
}

.detail-divider {
  margin: 20px 0 18px;
  border-top: 1px solid #E3E6ED;
}

.checkout-product {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 24px;

  &__image {
    flex: 0 0 auto;
    width: 76px;
    height: 76px;
    border-radius: 12px;
    background: #F4F6FB;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--empty {
      border: 1px solid #E1E6F0;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    margin-left: 18px;
  }

  &__name {
    color: #020B3A;
    font-size: 26px;
    line-height: 36px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sku {
    margin-top: 8px;
    color: #6B7285;
    font-size: 24px;
    line-height: 32px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.details-expand-enter-active,
.details-expand-leave-active {
  max-height: 720px;
  opacity: 1;
  overflow: hidden;
  transform: translateY(0);
  transition: max-height 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease, transform 0.32s ease, padding 0.32s ease, margin 0.32s ease;
  will-change: max-height, opacity, transform;
}

.details-expand-enter,
.details-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
  padding-top: 0;
  transform: translateY(-8px);
}

.cod-notice__icon,
.safe-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cod-notice__icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: linear-gradient(135deg, #FFB45F, #FF6A00);

  span {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 3px solid #FFFFFF;
    transform: rotate(12deg);
  }
}

.cod-fee-notice {
  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    color: #6B3B00;
    font-size: 26px;
    line-height: 34px;
    font-weight: 700;
  }

  &__desc {
    margin-top: 8px;
    color: #6B3B00;
    font-size: 24px;
    line-height: 34px;
  }
}

.safe-card {
  &__icon {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: #E6F0FF;
    color: #1C63F2;
    font-size: 34px;
  }

  &__title {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    color: #0F1F45;
  }

  &__desc {
    margin-top: 8px;
    font-size: 24px;
    line-height: 32px;
    color: #6B7285;
  }
}

.footer-total {
  flex: 0 1 auto;
  min-width: 0;

  &__label {
    overflow: hidden;
    font-size: 26px;
    line-height: 34px;
    color: #5E6478;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__value {
    margin-top: 4px;
    overflow: hidden;
    font-size: 38px;
    line-height: 44px;
    font-weight: 400;
    color: #0B45FF;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.checkout-btn {
  flex: 1 0 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  max-width: 420px;
  height: 88px;
  min-width: 0;
  border-radius: 14px;
  background: #1259F3;
  color: #FFFFFF;
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;

  .van-icon {
    font-size: 34px;
  }

  &--cod {
    background: #FF7900;
  }

  &--store {
    background: #1664F5;
  }
}

.store-section {
  &__heading {
    color: #0B0B12;
    font-size: 30px;
    line-height: 40px;
    font-weight: 700;
  }

  &__state {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 28px 0 4px;
    color: #6B7285;
    font-size: 25px;
    line-height: 36px;

    &--error { color: #E54747; }
    button { border: 0; background: transparent; color: #1664F5; font-size: 25px; font-weight: 700; }
  }
}

.store-expand-enter-active,
.store-expand-leave-active {
  overflow: hidden;
  transition: max-height .32s ease, margin-top .32s ease, opacity .22s ease, transform .32s ease;
}

.store-expand-enter,
.store-expand-leave-to {
  max-height: 0;
  margin-top: 0;
  opacity: 0;
  transform: translateY(-12px);
}

.store-expand-enter-to,
.store-expand-leave {
  max-height: 1200px;
  opacity: 1;
  transform: translateY(0);
}

.store-list { margin-top: 18px; }

.store-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 14px;
  padding: 18px;
  border: 1px solid #E1E5EE;
  border-radius: 16px;
  background: #FFFFFF;
  color: #0B0B12;
  text-align: left;
  box-sizing: border-box;

  > .van-icon:first-child { color: #7657FF; font-size: 40px; }
  > .van-icon:last-child { margin-left: auto; color: #1664F5; font-size: 34px; }

  &--selected { border-color: #1664F5; background: #F7FAFF; }
  &--closed { opacity: .5; }

  &__content {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    strong { font-size: 27px; line-height: 36px; }
    small { overflow: hidden; color: #6B7285; font-size: 22px; line-height: 30px; text-overflow: ellipsis; white-space: nowrap; }
  }
}

.cod-verification {
  padding: 36px 34px 44px;
  background: #FFFFFF;
  box-sizing: border-box;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;

  }

  &__header-copy {
    display: flex;
    min-width: 0;
    flex: 1;
  }

  &__close {
    flex: 0 0 auto;
    padding: 4px 0 0;
    border: 0;
    background: transparent;

    .van-icon { color: #111827; font-size: 36px; }
  }

  &__title {
    font-size: 34px;
    line-height: 44px;
    font-weight: 800;
    color: #0B0B12;
  }

  &__desc {
    margin-top: 10px;
    font-size: 26px;
    line-height: 38px;
    color: #6B7285;
  }

  &__phone {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-top: 28px;
    padding: 20px 24px;
    border-radius: 14px;
    background: #F7F9FC;
    font-size: 26px;
    line-height: 36px;
    color: #6B7285;

    strong {
      color: #111827;
      font-weight: 700;
    }
  }

  &__label {
    margin-top: 30px;
    font-size: 26px;
    line-height: 36px;
    font-weight: 700;
    color: #111827;
  }

  &__code-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 14px;

    input {
      flex: 1;
      min-width: 0;
      height: 86px;
      padding: 0 24px;
      border: 1px solid #E5E7EF;
      border-radius: 14px;
      background: #FFFFFF;
      box-sizing: border-box;
      color: #111827;
      font-size: 36px;
      line-height: 40px;
      letter-spacing: 0;
      outline: none;
    }

    button {
      flex-shrink: 0;
      min-width: 168px;
      height: 86px;
      padding: 0 18px;
      border: 0;
      border-radius: 14px;
      background: #FFF1E1;
      color: #FF7900;
      font-size: 26px;
      line-height: 34px;
      font-weight: 700;

      &:disabled {
        color: #9CA3AF;
        background: #F1F3F7;
      }
    }
  }

  &__tip {
    margin-top: 16px;
    font-size: 24px;
    line-height: 34px;
    color: #8A90A3;
  }

  &__confirm {
    width: 100%;
    height: 88px;
    margin-top: 32px;
    border: 0;
    border-radius: 14px;
    background: #FF7900;
    color: #FFFFFF;
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;

    &:disabled {
      background: #D1D5DB;
    }
  }

  &--store {
    padding: 16px 36px calc(36px + env(safe-area-inset-bottom));

    .cod-verification__handle {
      width: 76px;
      height: 9px;
      margin: 0 auto 20px;
      border-radius: 999px;
      background: #DCE1EC;
    }

    .cod-verification__header { gap: 12px; }
    .cod-verification__store-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 80px;
      width: 80px;
      height: 80px;
      margin-right: 14px;
      border-radius: 18px;
      background: #F1EEFF;
      color: #7657FF;

      .van-icon { font-size: 46px; color: #7657FF; }
    }
    .cod-verification__title { font-size: 30px; line-height: 36px; }
    .cod-verification__desc { margin-top: 6px; font-size: 23px; line-height: 30px; }
    .cod-verification__close--store {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      margin-top: 0;
      padding: 0;
      border-radius: 50%;
      background: #F1F3F7;
      .van-icon { font-size: 28px; }
    }
    .cod-verification__phone { margin-top: 20px; padding: 18px 20px; background: #F4F8FF; }
    .cod-verification__label { margin-top: 20px; }
    .cod-verification__code-row { margin-top: 12px; }
    .cod-verification__confirm { margin-top: 24px; background: #3D8CFF; }
    .cod-verification__code-row button { background: #EAF2FF; color: #3D8CFF; }
  }
}
</style>

